/**
 * Phase 7 Plan 03 — /exports page (D-150 + D-151 + D-152).
 *
 * Four sections per D-150:
 *   1. Asset roster (EXPORT-10.2) — Download → downloadAssetRoster()
 *   2. Current state (EXPORT-10.3) — Download → downloadCurrentState()
 *   3. Inspection log (EXPORT-10.4) — radio toggle (all-time | date range) +
 *      2 LTR-anchored <input type="date"> + Download → downloadInspectionLog(qs)
 *   4. Filtered map view (EXPORT-10.6 cut from v1 per D-152) — V2 note only
 *
 * RTL discipline: every layout class is a logical-property utility (T-7-03-04 —
 * `ps-`/`pe-`/`pbs-`/`pbe-`/`text-start`/`mbe-`/`mbs-`); the date inputs are
 * LTR-anchored even in AR locale per Phase 6 D-138 precedent (browser-native
 * <input type="date"> renders differently per locale; pinning to dir="ltr"
 * keeps the visible day/month/year ordering predictable for the operator).
 *
 * Errors: server validation `from > to` returns 422 + ERR_DATE_RANGE_INVALID
 * (D-19 envelope); the page renders the localized message via FormattedMessage
 * inside an alert region so AT (assistive tech) announces it.
 */
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "../../components/Button";
import {
  downloadAssetRoster,
  downloadCurrentState,
  downloadInspectionLog,
} from "../../api/exports";

type InspectionMode = "all" | "range";

export function ExportsPage() {
  const [mode, setMode] = useState<InspectionMode>("all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  async function trigger(
    section: string,
    fn: () => Promise<void>,
  ): Promise<void> {
    setErrorCode(null);
    setPending(section);
    try {
      await fn();
    } catch (err) {
      const code =
        err instanceof Error && err.message ? err.message : "ERR_GENERIC";
      setErrorCode(code);
    } finally {
      setPending(null);
    }
  }

  function buildInspectionLogQuery(): string {
    if (mode === "all") return "?all_time=true";
    return `?date_from=${dateFrom}&date_to=${dateTo}`;
  }

  return (
    <div className="ps-8 pe-8 pbs-8 pbe-8" data-testid="exports-page">
      <header className="mbe-6">
        <h1 className="text-2xl font-semibold text-on-surface">
          <FormattedMessage id="LABEL_EXPORT_PAGE_HEADING" />
        </h1>
      </header>

      {errorCode && (
        <div
          role="alert"
          data-testid="exports-error"
          className="mbe-4 ps-3 pe-3 pbs-2 pbe-2 border-s-4 border-error bg-error-container text-error text-sm rounded-md"
        >
          <FormattedMessage id={errorCode} />
        </div>
      )}

      {/* Section 1 — Asset roster (EXPORT-10.2) */}
      <section
        data-testid="export-asset-roster-section"
        className="mbe-6 pbs-4 pbe-4 ps-4 pe-4 bg-surface-raised border border-divider rounded-md"
      >
        <h2 className="text-lg font-semibold text-on-surface mbe-2">
          <FormattedMessage id="LABEL_EXPORT_ASSET_ROSTER_HEADING" />
        </h2>
        <p className="text-sm text-on-surface-variant mbe-3">
          <FormattedMessage id="LABEL_EXPORT_ASSET_ROSTER_DESCRIPTION" />
        </p>
        <Button
          onClick={() =>
            void trigger("asset_roster", () => downloadAssetRoster())
          }
          disabled={pending === "asset_roster"}
          data-testid="export-asset-roster-download"
        >
          <FormattedMessage id="LABEL_EXPORT_DOWNLOAD_BUTTON" />
        </Button>
      </section>

      {/* Section 2 — Current state (EXPORT-10.3) */}
      <section
        data-testid="export-current-state-section"
        className="mbe-6 pbs-4 pbe-4 ps-4 pe-4 bg-surface-raised border border-divider rounded-md"
      >
        <h2 className="text-lg font-semibold text-on-surface mbe-2">
          <FormattedMessage id="LABEL_EXPORT_CURRENT_STATE_HEADING" />
        </h2>
        <p className="text-sm text-on-surface-variant mbe-3">
          <FormattedMessage id="LABEL_EXPORT_CURRENT_STATE_DESCRIPTION" />
        </p>
        <Button
          onClick={() =>
            void trigger("current_state", () => downloadCurrentState())
          }
          disabled={pending === "current_state"}
          data-testid="export-current-state-download"
        >
          <FormattedMessage id="LABEL_EXPORT_DOWNLOAD_BUTTON" />
        </Button>
      </section>

      {/* Section 3 — Inspection log (EXPORT-10.4 + D-151 date-range picker) */}
      <section
        data-testid="export-inspection-log-section"
        className="mbe-6 pbs-4 pbe-4 ps-4 pe-4 bg-surface-raised border border-divider rounded-md"
      >
        <h2 className="text-lg font-semibold text-on-surface mbe-2">
          <FormattedMessage id="LABEL_EXPORT_INSPECTION_LOG_HEADING" />
        </h2>
        <p className="text-sm text-on-surface-variant mbe-3">
          <FormattedMessage id="LABEL_EXPORT_INSPECTION_LOG_DESCRIPTION" />
        </p>

        <div
          role="radiogroup"
          aria-label="inspection-log-mode"
          className="flex flex-col gap-2 mbe-3"
        >
          <label className="inline-flex items-center gap-2 text-sm text-on-surface">
            <input
              type="radio"
              name="inspection-log-mode"
              value="all"
              checked={mode === "all"}
              onChange={() => setMode("all")}
              data-testid="export-inspection-log-mode-all"
            />
            <FormattedMessage id="LABEL_EXPORT_ALL_TIME_RADIO" />
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-on-surface">
            <input
              type="radio"
              name="inspection-log-mode"
              value="range"
              checked={mode === "range"}
              onChange={() => setMode("range")}
              data-testid="export-inspection-log-mode-range"
            />
            <FormattedMessage id="LABEL_EXPORT_DATE_RANGE_RADIO" />
          </label>
        </div>

        {mode === "range" && (
          <div className="flex flex-wrap gap-3 mbe-3">
            <label className="inline-flex flex-col gap-1 text-sm text-on-surface-variant">
              <span>
                <FormattedMessage id="LABEL_EXPORT_DATE_RANGE_FROM" />
              </span>
              {/*
                Phase 6 D-138 precedent: <input type="date"> renders
                browser-native day/month/year picker and the layout stays
                LTR-anchored even in AR locale. The Tailwind ESLint rule is
                concerned with logical-property utility classes, not the
                native input dir attribute.
              */}
              <input
                type="date"
                dir="ltr"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="export-inspection-log-from"
                className="ps-2 pe-2 pbs-1 pbe-1 border border-divider rounded-md"
              />
            </label>
            <label className="inline-flex flex-col gap-1 text-sm text-on-surface-variant">
              <span>
                <FormattedMessage id="LABEL_EXPORT_DATE_RANGE_TO" />
              </span>
              <input
                type="date"
                dir="ltr"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="export-inspection-log-to"
                className="ps-2 pe-2 pbs-1 pbe-1 border border-divider rounded-md"
              />
            </label>
          </div>
        )}

        <Button
          onClick={() =>
            void trigger("inspection_log", () =>
              downloadInspectionLog(buildInspectionLogQuery()),
            )
          }
          disabled={pending === "inspection_log"}
          data-testid="export-inspection-log-download"
        >
          <FormattedMessage id="LABEL_EXPORT_DOWNLOAD_BUTTON" />
        </Button>
      </section>

    </div>
  );
}
